import { useNavigate, Link } from 'react-router-dom';
import { useAtomValue, useSetAtom } from 'jotai';
import { authAtom, logoutAtom } from '../../store/auth';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { UserAvatar } from '@/components/ui/user-avatar';
import { clearAvatarCache } from '@/utils/avatar';
import { UserRole } from '../../constants/roles';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { useAdminPendingRequests, usePendingToolRequests, useApproverPendingRequests } from '@/hooks/useCachedQueries';

interface AppLayoutProps {
  children: React.ReactNode;
}

export const AppLayout = ({ children }: AppLayoutProps) => {
  const navigate = useNavigate();
  const { user } = useAtomValue(authAtom);
  const logout = useSetAtom(logoutAtom);

  // Use centralized queries to eliminate duplicate API calls
  const { data: approverPendingData } = useApproverPendingRequests();
  const approverPendingCount = approverPendingData?.pagination?.total || 0;

  const { data: adminPendingAccessData } = useAdminPendingRequests();
  const adminPendingAccessCount = adminPendingAccessData?.pagination?.total || 0;

  const { data: adminNewToolRequestsData } = usePendingToolRequests();
  const adminNewToolRequestsCount = adminNewToolRequestsData?.length || 0;

  const totalAdminActionCount = adminPendingAccessCount + adminNewToolRequestsCount;

  const handleLogout = () => {
    logout();
    clearAvatarCache();
    window.location.href = '/login';
  };

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  return (
    <>
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-16 items-center justify-between">
          <h1 className="text-xl font-semibold tracking-tight text-primary">
            <Link to="/dashboard" className="hover:opacity-80 transition-opacity">
              Modus App Directory
            </Link>
          </h1>
          
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <DropdownMenu.Root>
              <DropdownMenu.Trigger className="relative h-9 w-9 rounded-full border-2 border-primary/10 bg-transparent transition-colors hover:border-primary/20">
                <UserAvatar 
                  user={user} 
                  className="h-full w-full"
                />
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content 
                  className="z-50 min-w-[12rem] overflow-hidden rounded-md border bg-popover p-2 text-popover-foreground shadow-md animate-in data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2"
                  align="end"
                >
                  <div className="border-b px-2 pb-2 pt-1">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                  <div className="p-1">
                    <DropdownMenu.Item 
                      className="relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground"
                      onSelect={() => handleNavigate('/dashboard')}
                    >
                      Dashboard
                    </DropdownMenu.Item>
                    {user && (user.role === UserRole.ADMIN || user.isApprover) && (
                      <DropdownMenu.Item 
                        className="relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground"
                        onSelect={() => handleNavigate('/approver')}
                      >
                        Approver Dashboard
                        {approverPendingCount > 0 && (
                          <Badge variant="destructive" className="ml-1.5 px-1.5 py-0.5 text-xs rounded-full">
                            {approverPendingCount}
                          </Badge>
                        )}
                      </DropdownMenu.Item>
                    )}
                    {user?.role === UserRole.ADMIN && (
                      <DropdownMenu.Item 
                        className="relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground"
                        onSelect={() => handleNavigate('/admin')}
                      >
                        Admin Dashboard
                        {totalAdminActionCount > 0 && (
                          <Badge variant="destructive" className="ml-1.5 px-1.5 py-0.5 text-xs rounded-full">
                            {totalAdminActionCount}
                          </Badge>
                        )}
                      </DropdownMenu.Item>
                    )}
                  </div>
                  <div className="border-t p-1">
                    <DropdownMenu.Item 
                      className="relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-destructive hover:text-destructive-foreground focus:bg-destructive focus:text-destructive-foreground"
                      onSelect={handleLogout}
                    >
                      Logout
                    </DropdownMenu.Item>
                  </div>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          </div>
        </div>
      </header>
      <main className="flex-1">
        {children}
      </main>
    </>
  );
}; 